import React from "react";
import { Col, Container, Row } from "react-bootstrap";
import Accordion from "@app/components/Accordion";
import List from "@app/components/List";
import Panel from "@app/components/Panel";
import { NumericFormat } from "react-number-format";
import { Link } from "react-router-dom";
import getDisplayFields from "@app/lib/hooks/getDisplayFields";
import { filterElements, formatDate } from "@app/utils/helper";
import * as styles from "./FormSummaryElements.module.scss";
import classNames from "classnames";

export const InnerList = ({
	values,
	errors,
	parentPath,
	elements,
	archiveMode,
	printMode,
	element,
	index,
	getValue,
}) => {
	return (
		<List
			isIndexed={false}
			isSummary={true}
			noBackground={true}
			condensed={true}
			items={getDisplayFields(elements, values).map((element1, index1) => {
				const getEplValue = () => {
					if (element1?.formGroup?.elements?.[0]?.elementType === "extendedPropertyList") {
						return "";
					}
					if (element1.elementType === "accordion" && "formGroup" in element1) {
						return getValue(
							element,
							element1?.formGroup?.elements?.[0]?.name,
							element1?.formGroup?.elements?.[0]?.elementType,
							element1?.formGroup?.elements?.[0]?.additional?.options,
						);
					}
					return getValue(element, element1.name, element1.elementType, element1?.additional?.options);
				};
				const error = errors[parentPath ? `${parentPath}[${index}].${element1.name}` : element1.name];
				const errorIndicatorClassName = classNames({
					[styles["error"]]: error && !archiveMode && !printMode,
					[styles[`error--${error?.category}`]]: error?.category && !archiveMode && !printMode,
				});
				return (
					<Container className={classNames("content", errorIndicatorClassName)} key={index1}>
						<Row>
							<Col xs={12} lg={6}>
								<span className={"fw-bold"} dangerouslySetInnerHTML={{ __html: element1.label }} />
							</Col>
							<Col xs={12} lg={6}>
								{getEplValue()}
								{error?.type === "required" && (
									<div className={"hide-on-print"}>
										{formStepLink ? (
											<Link to={`${formStepLink}#${name}`} state={{ summaryLink: true }}>
												{error.message}
											</Link>
										) : (
											<span>{error.message}</span>
										)}
									</div>
								)}
							</Col>
						</Row>
					</Container>
				);
			})}
		/>
	);
};

export const ExtendedPropertyListSummary = ({
	elementType,
	name,
	label,
	getValue,
	value,
	values,
	getDisplayFields,
	printMode,
	archiveMode = false,
	type,
	errors,
	formStepLink,
	parentPath = "",
	...specialProps
}) => {
	const currentValue = value || getValue(values, name, elementType) || [];
	if (!Array.isArray(currentValue) || currentValue?.length === 0) return "";
	const element = specialProps.formGroup.elements[0];
	return (
		<Container className={"content"}>
			{label && <h2 className={"mb-12 mt-24"} dangerouslySetInnerHTML={{ __html: label }} />}
			{specialProps.formGroup?.elements?.length === 1 && (
				<Row>
					<Col xs={12} lg={6}>
						<span
							className={"fw-bold"}
							dangerouslySetInnerHTML={{ __html: element.labelSummary || element.label }}
						/>
					</Col>
					<Col xs={12} lg={6}>
						{currentValue.map((entry) => (
							<>
								{entry[element.name]}
								<br />
							</>
						))}
					</Col>
				</Row>
			)}
			{specialProps.formGroup?.elements?.length > 1 &&
				currentValue.map((element, index) => {
					const headerElement = specialProps?.formGroup?.elements?.find(({ showAsHeader }) => !!showAsHeader);
					let headline = element[headerElement?.name];

					if (headerElement && headerElement.elementType === "accordion") {
						headline = element[headerElement?.formGroup?.elements?.[0]?.name];
						if (Array.isArray(headline)) {
							headline = headline.join(", ");
						}
					}

					return (
						<Accordion key={index} headline={headline} initialOpen={index === 0} showInstant={printMode}>
							<InnerList
								specialProps={specialProps}
								element={element}
								elements={specialProps.formGroup.elements}
								values={values}
								errors={errors}
								printMode={printMode}
								archiveMode={archiveMode}
								parentPath={parentPath}
								index={index}
								getValue={getValue}
							/>
						</Accordion>
					);
				})}
		</Container>
	);
};

const SubFormPageSummary = ({
	elementType,
	name,
	formPage,
	label,
	getValue,
	values,
	getDisplayFields,
	printMode,
	type,
	errors,
	formStepLink,
	parentPath = "",
	...specialProps
}) => {
	if (specialProps?.extendedFormKey !== "gefaehrdungsanalyse") return "";

	const subFormValues = getValue(values, name, elementType);

	if (!Array.isArray(subFormValues) || subFormValues?.length === 0) return "";

	return (
		<Container className={"content"}>
			{label && <h2 className={"mb-12 mt-24"}>{label}</h2>}
			{subFormValues.map((subFormValue, index) => {
				let labelPrefix;
				switch (subFormValue?.physischegefaehrdungsanalyse_kategorie_kategorie) {
					case "physische_gefährdung":
						labelPrefix = "Gefährdungsfaktor: ";
						break;
					case "einfluss_am_tätigkeitsort":
						labelPrefix = "Einfluss: ";
						break;
					case "gefahrenstoff":
						labelPrefix = "Gefahrenstoff: ";
						break;
					default:
						labelPrefix = "";
				}

				const OptionalAccordion = specialProps.noHeader ? React.Fragment : Accordion;

				return (
					<OptionalAccordion
						key={index}
						headline={`${labelPrefix}${subFormValue.physischegefaehrdungsanalyse_gefaehrdungstyp_physische_gefaehrdung}${subFormValue.physischegefaehrdungsanalyse_gefaehrdungstyp_einflussamtaetigkeitsort}${subFormValue.physischegefaehrdungsanalyse_gefaehrdungstyp_gefahrenstoff}`}
						initialOpen={index === 0}
						showInstant={printMode}
					>
						{formPage?.fieldsets?.map((fieldset, fieldsetIndex) => (
							<FormSummaryElements
								key={fieldsetIndex}
								headline={fieldset.headline}
								elements={
									getDisplayFields(fieldset.elements, subFormValue).filter((element) =>
										filterElements(element, type),
									) || []
								}
								values={subFormValue ?? {}}
								errors={errors ?? []}
								type={type}
								formStepLink={formStepLink}
								printMode={printMode}
								parentPath={parentPath.match(/\[\d+\]$/) ? parentPath : `${parentPath}[${index}]`}
							/>
						))}
					</OptionalAccordion>
				);
			})}
		</Container>
	);
};

const AccordionSummary = ({
	elementType,
	name,
	formPage,
	label,
	getValue,
	values,
	getDisplayFields,
	printMode,
	type,
	errors,
	formStepLink,
	...specialProps
}) => {
	const displayFields = getDisplayFields(specialProps.formGroup.elements, values);
	return (
		<Container className={"content"}>
			<Accordion
				headline={`${label || specialProps.labelSummary}`}
				initialOpen={false}
				showInstant={printMode}
				className={styles["accordion-summary"]}
			>
				<FormSummaryElements
					// headline={fieldset.headline}
					elements={displayFields || []}
					values={values}
					errors={errors}
					type={type}
					formStepLink={formStepLink}
					printMode={printMode}
					isIndexed={false}
					isSummary={true}
					noBackground={true}
					condensed={true}
					className={styles["signs-list"]}
				/>
			</Accordion>
		</Container>
	);
};

const SignsList = ({ value, element }) => {
	return value.map((element1, index1) => {
		const icon = element.options.find(({ value }) => value === element1)?.icon;
		return (
			<div className={styles["signs-list-entry"]}>
				{icon && <img src={icon} alt={element.label} />}
				{element1}
			</div>
		);
	});
};

const FormSummaryElements = ({
	headline,
	elements,
	values,
	errors,
	type,
	formStepLink,
	printMode,
	archiveMode = false,
	onSignatureLoaded,
	className,
	isFollowUp,
	parentPath = "",
}) => {
	const getValue = (values, name, elementType, options, onLoadSignature) => {
		if (!(name in values)) {
			return "";
		}

		const value = values[name];

		switch (elementType) {
			case "formattedNumber":
				return <NumericFormat displayType={"text"} value={value} />;
			case "checkbox":
			case "checkboxTeaser":
				//@todo: hardcoded terms
				return typeof value === "string" && value.length > 0 ? value : Boolean(value) === true ? "Ja" : "Nein";
			case "select":
			case "radio":
			case "radioGroup":
			case "radioSwitcher":
				return value && options ? (
					<>
						{options.find((option) => option.value === value).label} ({value})
					</>
				) : Array.isArray(value) ? (
					value.join(", ")
				) : (
					value
				);
			case "attributeSelector":
				return Array.isArray(value) ? value.join(", ") : value;
			case "repeatableList":
				return value.map((item) => Object.values(item).join(" ")).join(", ");
			case "signature":
				if (!value) onLoadSignature(false);
				return (
					<img
						src={value}
						alt={"Unterschrift"}
						className={"signature"}
						onLoad={() => onLoadSignature(true)}
						onError={() => onLoadSignature(false)}
					/>
				);
			case "datepicker":
				return formatDate(value);
			case "radioSwitcherList":
				return Object.values(value).join(",");
			default:
				return value;
		}
	};
	return (
		<>
			{headline && <h2 className={"mb-12 mt-24"}>{headline}</h2>}
			<List
				className={className}
				isIndexed={false}
				isSummary={true}
				isFollowUp={true}
				noBackground={true}
				condensed={true}
				items={getDisplayFields(elements, values).map((element, elementIndex) => {
					const {
						elementType,
						name,
						label: elementLabel,
						labelSummary,
						labelSummery,
						...specialProps
					} = element;
					const label = labelSummary || labelSummery || elementLabel;
					return (
						<React.Fragment key={elementIndex}>
							{(() => {
								const errorPath = parentPath ? `${parentPath}.${name}` : name;
								const error = !archiveMode && !printMode && errors[errorPath];
								const errorIndicatorClassName = classNames({
									[styles["error"]]: error && !archiveMode && !printMode,
									[styles[`error--${error?.category}`]]:
										error?.category && !archiveMode && !printMode,
								});
								switch (name) {
									case "warnhinweise_warnhinweise_verbotszeichen":
									case "warnhinweise_warnhinweise_gebotszeichen":
									case "warnhinweise_warnhinweise_warnzeichen":
									case "warnhinweise_warnhinweise_brandschutzzeichen":
									case "warnhinweise_warnhinweise_rettungszeichen":
									case "warnhinweise_warnhinweise_gefahrgutkennzeichen": {
										const lastName = name.split("_")[2];
										const fieldName = `${name}_${lastName}`;
										const fieldValue = values[fieldName];

										return fieldValue !== undefined ? (
											<AccordionSummary
												{...element}
												getValue={getValue}
												values={values}
												getDisplayFields={getDisplayFields}
												type={type}
												printMode={printMode}
												errors={errors}
												// formStepLink={formStepLink}
											></AccordionSummary>
										) : null;
									}
									case "warnhinweise_warnhinweise_verbotszeichen_verbotszeichen":
									case "warnhinweise_warnhinweise_gebotszeichen_gebotszeichen":
									case "warnhinweise_warnhinweise_warnzeichen_warnzeichen":
									case "warnhinweise_warnhinweise_brandschutzzeichen_brandschutzzeichen":
									case "warnhinweise_warnhinweise_rettungszeichen_rettungszeichen":
									case "warnhinweise_warnhinweise_gefahrgutkennzeichen_gefahrgutkennzeichen":
										return <SignsList value={values[name]} element={element} />;
									case "taetigkeit_eignungsuntersuchungen_form":
										const formGroup = { ...element.formGroup };
										formGroup.elements = [
											formGroup.elements.find(
												({ name }) =>
													name === "taetigkeit_eignungsuntersuchungen_form_zustaendig",
											),
										];
										const providerElement = formGroup.elements.find(
											({ name }) => name === "taetigkeit_eignungsuntersuchungen_form_zustaendig",
										);
										const headline = formGroup.elements.find(
											({ name }) => name === "taetigkeit_eignungsuntersuchungen_form_auswahl",
										);

										return (
											<div className={styles["examinations-list-container"]}>
												<List
													isIndexed={false}
													isSummary={true}
													noBackground={true}
													condensed={true}
													items={Object.entries(values[name] || {}).map(
														([key, entry], index) => {
															const examination =
																entry["taetigkeit_eignungsuntersuchungen_form_auswahl"];
															const examinationCopy =
																examination !== "Sonstige"
																	? examination
																	: `${examination}: ${entry["taetigkeit_eignungsuntersuchungen_form_sonstige"]}`;

															return (
																<Container className={"content"} key={index}>
																	<Row>
																		<Col xs={12} lg={6} className={"fw-bold"}>
																			{examinationCopy}
																			<br />
																			{providerElement.labelSummary ||
																				providerElement.label}
																		</Col>
																		<Col
																			xs={12}
																			lg={6}
																			className={"d-flex align-items-end"}
																		>
																			{entry[
																				"taetigkeit_eignungsuntersuchungen_form_zustaendig"
																			] || "-"}
																		</Col>
																	</Row>
																</Container>
															);
														},
													)}
													className={styles["examinations-list"]}
												/>
											</div>
										);
								}
								switch (elementType) {
									case "followUp":
										return (
											<Panel
												className={classNames(styles["followup-summary"], "followup-summary")}
												hasBg={true}
												header={{
													colored: true,
													label: specialProps.formGroup.title,
												}}
											>
												<FormSummaryElements
													values={values}
													elements={element?.formGroup?.elements || []}
													errors={errors}
													type={type}
													printMode={printMode}
													isIndexed={false}
													isSummary={true}
													noBackground={true}
													condensed={true}
													isFollowUp={true}
												/>
											</Panel>
										);

									case "extendedPropertyList":
										return (
											<ExtendedPropertyListSummary
												{...element}
												getValue={getValue}
												values={values}
												getDisplayFields={getDisplayFields}
												type={type}
												printMode={printMode}
												errors={errors}
												formStepLink={formStepLink}
												parentPath={`${name}`}
											/>
										);
									case "subFormPage":
										return (
											<SubFormPageSummary
												{...element}
												getValue={getValue}
												values={values}
												getDisplayFields={getDisplayFields}
												type={type}
												printMode={printMode}
												errors={errors}
												formStepLink={formStepLink}
												parentPath={parentPath || `${name}`}
											/>
										);
									case "accordion":
										return (
											<AccordionSummary
												{...element}
												getValue={getValue}
												values={values}
												getDisplayFields={getDisplayFields}
												type={type}
												printMode={printMode}
												errors={errors}
												// formStepLink={formStepLink}
											/>
										);
									case "radioSwitcherList":
										return (
											<Container className={classNames("content", errorIndicatorClassName)}>
												<Row>
													<Col xs={12}>
														<div className={"fw-bold mb-12"} data-key={name}>
															{label}
														</div>
														{error?.type === "required" && (
															<div className={"hide-on-print"}>
																<br />
																{formStepLink ? (
																	<Link
																		to={`${formStepLink}#${name}`}
																		state={{ summaryLink: true }}
																	>
																		{error.message}
																	</Link>
																) : (
																	<span>{error.message}</span>
																)}
															</div>
														)}
														<List
															isIndexed={false}
															isSummary={true}
															noBackground={true}
															condensed={true}
															items={Object.entries(values[name] || {}).map(
																([key, entry], index) => {
																	return (
																		<Container className={"content"} key={index}>
																			<Row>
																				<Col
																					xs={12}
																					lg={6}
																					className={"fw-bold"}
																				>
																					{key}
																					<br />
																					{element.headerLabel}
																				</Col>
																				<Col
																					xs={12}
																					lg={6}
																					className={"d-flex align-items-end"}
																				>
																					{entry || "-"}
																				</Col>
																			</Row>
																		</Container>
																	);
																},
															)}
														/>
													</Col>
												</Row>
											</Container>
										);

									case "hint":
										return null;

									default:
										return label ? (
											<Container className={classNames("content", errorIndicatorClassName)}>
												<Row>
													<Col xs={12} lg={6}>
														<span
															className={"fw-bold"}
															data-key={name}
															dangerouslySetInnerHTML={{
																__html: label ? label : "!! no label !!",
															}}
														/>
													</Col>
													<Col xs={12} lg={6}>
														{getValue(
															values,
															name,
															elementType,
															specialProps?.additional?.options,
															onSignatureLoaded,
														)}
														{error?.type === "required" && (
															<div className={"hide-on-print"}>
																{formStepLink ? (
																	<Link
																		to={`${formStepLink}#${name}`}
																		state={{ summaryLink: true }}
																	>
																		{error.message}
																	</Link>
																) : (
																	<span>{error.message}</span>
																)}
															</div>
														)}
													</Col>
												</Row>
											</Container>
										) : null;
								}
							})()}
						</React.Fragment>
					);
				})}
			/>
		</>
	);
};

FormSummaryElements.propTypes = {};

export default FormSummaryElements;
